import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState, useEffect } from "react";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { SubjectTypeIcon } from "../SubjectTypeIcon";
import { DateTime } from "luxon";
import theme from "../../theme";
import { useGetProfileList } from "../../data/useGetProfileList";
import { Profile } from "../../types/Profile";
import { PagedResponse } from "../../types/Common";
import { StyledLink } from "../Breadcrumbs";
import { Loader } from "../Loader";
import { Paragraph } from "../Paragraph";
import { ReviewStatusPill } from "../ReviewStatusPill";
import {
  ProfileStatusColours,
  ProfileStatusBgColours,
} from "../layoutConfig";
import { ProfileStatusSlug } from "../../context/ProfileStatusContext";
import "./profileTable.css";
import {
  useServerTable,
  TablePagination,
  SortIndicator,
  pageCountFor,
} from "../ServerTable";
// import { NoScopeSelectedNotice } from "../NoScopeSelectedNotice";

export const ProfilesTable = (props) => {
  const {
    searchTerm,
    filters,
    type,
    initialSorting,
    initialPageSize,
    onSortChange,
    onPageSizeChange,
  } = props;

  const [data, setData] = useState<Partial<PagedResponse<Profile[]>>>({});
  const [filtersUpdate, setFiltersUpdate] = useState({ ...filters });
  const [searchTermUpdate, setSearchTermUpdate] = useState(props.searchTerm);
  const [profileType] = useState(type);

  const cols: ColumnDef<Profile>[] = [
    props.type === "entity"
      ? {
          header: () => "Name",
          accessorKey: "name",
          size: 700,
          minSize: 300,
          enableSorting: true,
          cell: (info) => {
            const { name } = info.row.original;
            return (
              <Flex alignItems="center" gap="8">
                <SubjectTypeIcon isEntity />
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "15px",
                    color: theme.colors.darkBlue,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                  }}
                  title={name}
                >
                  {name}
                </span>
              </Flex>
            );
          },
        }
      : props.type === "person"
      ? {
          header: () => "Name",
          accessorKey: "first_name",
          size: 700,
          minSize: 300,
          enableSorting: true,
          cell: (info) => {
            const { first_name, middle_name, last_name } = info.row.original;
            const fullName =
              [first_name, middle_name, last_name].filter(Boolean).join(" ") ||
              "-";

            return (
              <Flex alignItems="center" gap="8">
                <SubjectTypeIcon isEntity={false} />
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "15px",
                    color: theme.colors.darkBlue,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                  }}
                  title={fullName}
                >
                  {fullName}
                </span>
              </Flex>
            );
          },
        }
      : {
          header: () => "Name",
          accessorKey: "name",
          size: 700,
          minSize: 300,
          enableSorting: true,
          cell: (info) => {
            const { _type, first_name, last_name, name } = info.row.original;
            const isEntity = _type === "entity";
            const displayName = isEntity
              ? name
              : `${first_name} ${last_name}`;
            return (
              <Flex alignItems="center" gap="8">
                <SubjectTypeIcon isEntity={isEntity} />
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "15px",
                    color: theme.colors.darkBlue,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                  }}
                  title={displayName}
                >
                  {displayName}
                </span>
              </Flex>
            );
          },
        },

    {
      header: () => "Status",
      id: "status",
      accessorKey: "status",
      size: 180,
      minSize: 160,
      maxSize: 220,
      enableSorting: false,
      cell: (info) => {
        const { status } = info.row.original;

        if (!status) {
          return <Flex color={theme.colors.gray[40]}>-</Flex>;
        }

        const slug = (status.status ?? "") as ProfileStatusSlug;
        const color = ProfileStatusColours[slug] ?? "gray.60";
        const bgColor = ProfileStatusBgColours[slug] ?? "gray.10";

        return (
          <ReviewStatusPill
            label={status.label ?? "-"}
            color={color}
            bgColor={bgColor}
          />
        );
      },
    },
    {
      header: () => "Division",
      accessorKey: "primary_division",
      size: 150,
      minSize: 120,
      maxSize: 200,
      enableSorting: false,
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {info.row.original.primary_division?.display_name ?? "-"}
          </Flex>
        );
      },
    },
    {
      header: () => "Team",
      accessorKey: "primary_team",
      size: 150,
      minSize: 120,
      maxSize: 200,
      enableSorting: false,
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {info.row.original.primary_team?.display_name ?? "-"}
          </Flex>
        );
      },
    },
    {
      header: () => "Reference",
      accessorKey: "reference",
      size: 180,
      minSize: 180,
      maxSize: 180,
      enableSorting: false,
      cell: (info) => {
        const { reference } = info.row.original;
        return (
          <Flex
            justifyContent="start"
            alignItems="center"
            position="relative"
            color={theme.colors.gray[40]}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={reference}
          >
            {reference}
          </Flex>
        );
      },
    },
    {
      header: () => "External Reference",
      accessorKey: "external_reference",
      size: 180,
      minSize: 180,
      maxSize: 180,
      enableSorting: false,
      cell: (info) => {
        const value = info.row.original.external_reference || "-";

        return (
          <Flex
            justifyContent="start"
            alignItems="center"
            position="relative"
            color={theme.colors.gray[40]}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={String(value)}
          >
            {String(value)}
          </Flex>
        );
      },
    },

    {
      id: "created_on",
      header: () => "Created",
      accessorKey: "created_on",
      size: 160,
      minSize: 160,
      maxSize: 160,
      enableSorting: true,
      cell: (info) => {
        const { created_on } = info.row.original;
        return (
          <Flex color={theme.colors.gray[40]}>
            {DateTime.fromJSDate(new Date(created_on as string)).toFormat(
              "dd/MM/yyyy"
            )}
          </Flex>
        );
      },
    },
    {
      header: () => "",
      id: "view_id",
      accessorKey: "id",
      size: 100,
      minSize: 100,
      maxSize: 100,
      enableSorting: false,
      cell: (info) => {
        const { id } = info.row.original;
        const path = `/profile/${id}?tab=details`;

        return (
          <StyledLink to={path}>
            <span style={{ color: theme.colors.blue.primary }}>View</span>
          </StyledLink>
        );
      },
    },
  ];

  const [columns] = useState([...cols]);
  const {
    pageIndex,
    pageSize,
    pagination,
    setPagination,
    sorting,
    setSorting,
    sortOrder,
    sortBy: orderBy,
    resetPagination,
  } = useServerTable(initialSorting, initialPageSize, onSortChange, onPageSizeChange);

  const profilesQuery = useGetProfileList({
    search: searchTermUpdate || filtersUpdate?.name || undefined,
    type: profileType || undefined,
    registrationNumber: filtersUpdate?.registrationNumber || undefined,
    dateOfBirth: filtersUpdate?.dateOfBirth || undefined,
    page: pageIndex + 1,
    pageSize,
    sortBy: sortOrder,
    orderBy,
  });

  useEffect(() => {
    resetPagination();
    setFiltersUpdate(filters || {});
    setSearchTermUpdate(searchTerm);
  }, [searchTerm, filters, resetPagination]);

  useEffect(() => {
    setData(() => profilesQuery?.data?.data);
  }, [profilesQuery]);

  const table = useReactTable({
    data: data?.results ?? [],
    columns,
    state: {
      pagination,
      sorting,
    },
    pageCount: pageCountFor(data?.count, pageSize),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  // Shared by the loader and the table so the pagination below stays in the
  // footer while data loads, instead of jumping up under a short loader.
  // 310px = the top bar, search row and type tabs above (~230px) plus the
  // in-flow pagination row below (~66px with its margins) and a little slack,
  // so the pagination stays fully on screen.
  const tableHeight = "calc(100vh - 310px)";

  return (
    <div>
      {/* Hidden for now — see NoScopeSelectedNotice. */}
      {/* <NoScopeSelectedNotice /> */}
      {/* One fixed-height area for loading, empty and loaded states, so the
          pagination below stays in the footer whichever one is showing. */}
      <Box height={tableHeight}>
        {profilesQuery.isFetching && (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <Loader />
          </Flex>
        )}

        {!profilesQuery.isFetching && data?.results?.length === 0 && (
          <Paragraph m={3} color="gray.40" textAlign="center">
            No profiles found
          </Paragraph>
        )}

        {!profilesQuery.isFetching && data?.results?.length > 0 && (
          <div
            className="tableContainer"
            style={{ height: tableHeight, overflowX: "auto" }}
          >
            <table className="profilesTable">
              <colgroup>
                <col style={{ width: "30%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "8%" }} />
              </colgroup>

              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder ? null : (
                          <div
                            className={
                              header.column.getCanSort()
                                ? "select-none cursor-pointer flex items-center gap-1"
                                : ""
                            }
                            onClick={header.column.getToggleSortingHandler()}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}

                            <SortIndicator column={header.column} />
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell, index) => (
                      <td
                        key={cell.id}
                        className={index === 0 ? "nameColumn" : "fixedColumn"}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Box>

      <TablePagination table={table} />
    </div>
  );
};
